/**
 * ShareRecipientsListView - Displays existing share recipients with revoke controls
 *
 * Renders a "Shared with" heading followed by a list of users showing avatar
 * initials, display name, role badge, and a revoke button for non-owner shares.
 * Used in both ReportShareSheet and future NoteShareSheet contexts.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { XIcon } from "lucide-react-native";
import { memo } from "react";
import { ScrollView } from "@/components/atoms/ScrollView";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Display data for a single share recipient */
export interface RecipientDisplayData {
  /** The user's unique ID */
  readonly userId: string;
  /** Full display name */
  readonly displayName: string;
  /** Two-letter initials for the avatar circle */
  readonly initials: string;
  /** Share role (e.g., "OWNER" or "VIEWER") */
  readonly role: string;
}

/** Props for ShareRecipientsListView */
interface ShareRecipientsListViewProps {
  /** Mapped share recipients to display */
  readonly recipients: readonly RecipientDisplayData[];
  /** Whether a revoke operation is currently in progress */
  readonly isRevoking: boolean;
  /**
   * Whether dark mode is active.
   * @remarks SE-6800b: no longer read internally — every class in this view
   * collapsed to a single semantic token that inverts on its own across
   * themes. Kept in the prop contract because
   * `ShareRecipientsListContainer` (outside this migration's scope) still
   * passes it.
   */
  readonly isDark: boolean;
  /** Pre-bound revoke handlers keyed by userId, avoids inline functions in JSX */
  readonly revokeHandlers: Readonly<Record<string, () => void>>;
}

/** Roles that cannot be revoked (owner has full access, manager has implicit access) */
const NON_REVOCABLE_ROLES = new Set(["OWNER", "MANAGER"]);

/** Foreground text token — collapses the former dark:text-white/light:text-zinc-900 fork */
const FG_TEXT_CLASS = "text-content-primary";

/** Muted text token — collapses the former dark:text-zinc-400/light:text-zinc-500 fork */
const MUTED_TEXT_CLASS = "text-content-muted";

/** Divider border token — collapses the former dark:border-zinc-700/light:border-zinc-200 fork */
const BORDER_CLASS = "border-outline-subtle";

/** Avatar circle background token — collapses the former dark:bg-zinc-700/light:bg-zinc-200 fork */
const AVATAR_BG_CLASS = "bg-surface-subtle";

/**
 * Role badge classes, keyed by share role. Each value collapses a former
 * dark/light class fork into a single semantic token pair.
 * SE-6800b: DEFAULT (VIEWER, etc.) overrides the tool's numeric winner
 * (bg-surface-muted) with the role-correct bg-status-info-surface — a
 * colored role badge reads as an "info" fill, not neutral chrome.
 */
const ROLE_BADGE_CLASSES: Record<string, string> = {
  OWNER: "bg-surface-subtle text-content-secondary",
  MANAGER: "bg-status-warning-icon-surface text-status-warning",
};
const DEFAULT_BADGE_CLASSES = "bg-status-info-surface text-status-info-strong";

/**
 * Returns the role badge classes for the given share role
 * @param role - The share role (OWNER, MANAGER, or VIEWER)
 */
const getRoleBadgeClass = (role: string): string =>
  ROLE_BADGE_CLASSES[role] ?? DEFAULT_BADGE_CLASSES;

/**
 * Renders a list of share recipients with role badges and revoke controls
 * @param root0 - Component props
 * @param root0.recipients - Mapped share recipients to display
 * @param root0.isRevoking - Whether a revoke operation is currently in progress
 * @param root0.revokeHandlers - Pre-bound revoke handlers keyed by userId
 */
const ShareRecipientsListView = ({
  recipients,
  isRevoking,
  revokeHandlers,
}: ShareRecipientsListViewProps) => (
  <Stack className="gap-3">
    <Heading variant="title-sm" className={FG_TEXT_CLASS}>
      Shared with
    </Heading>

    {recipients.length === 0 ? (
      <Box className="items-center justify-center py-4">
        <Text variant="body" className={MUTED_TEXT_CLASS}>
          Not shared with anyone yet
        </Text>
      </Box>
    ) : (
      <ScrollView bounces={false} className="max-h-48">
        <Stack className="gap-1">
          {recipients.map(recipient => (
            <Stack
              direction="horizontal"
              key={recipient.userId}
              className={`items-center gap-3 border-b py-2 ${BORDER_CLASS}`}
            >
              {/* Avatar circle with initials */}
              <Box
                className={`size-9 items-center justify-center rounded-full ${AVATAR_BG_CLASS}`}
              >
                <Text variant="caption-strong" className={MUTED_TEXT_CLASS}>
                  {recipient.initials}
                </Text>
              </Box>

              {/* Display name */}
              <Text
                variant="body"
                className={`flex-1 font-medium ${FG_TEXT_CLASS}`}
              >
                {recipient.displayName}
              </Text>

              {/* Role badge */}
              <Box
                className={`rounded-full px-2 py-micro ${getRoleBadgeClass(recipient.role)}`}
              >
                <Text variant="micro-strong">{recipient.role}</Text>
              </Box>

              {/* Revoke button - hidden for owner and manager roles */}
              {!NON_REVOCABLE_ROLES.has(recipient.role) && (
                <Pressable
                  onPress={revokeHandlers[recipient.userId]}
                  disabled={isRevoking}
                  className={`rounded-full p-1 ${isRevoking ? "opacity-40" : ""}`}
                >
                  <Icon as={XIcon} size="sm" className={MUTED_TEXT_CLASS} />
                </Pressable>
              )}
            </Stack>
          ))}
        </Stack>
      </ScrollView>
    )}
  </Stack>
);

ShareRecipientsListView.displayName = "ShareRecipientsListView";

export default memo(ShareRecipientsListView);
